// @workflow assistant.voice, assistant.attachments
/**
 * One fetch with the user's auth headers, resent once after a token refresh when the server says
 * 401: the request pattern of the assistant's REST endpoints outside the SSE stream (dictation,
 * Read aloud, attachments). Any other init field (an upload's `onProgress`) goes to fetchImpl as it is. Network errors and aborts are thrown as they are; each client words them.
 * fetch, headers and token refresh are injected, so node --test drives it with fakes.
 * No Vue or path-alias imports.
 */

const HTTP_UNAUTHORIZED = 401;

/**
 * @param {{ fetchImpl?: typeof fetch, getHeaders?: () => Record<string, string>, refreshAuth?: () => Promise<boolean> }} deps
 * @returns {(url: string, init: { method: string, body?: *, signal?: AbortSignal, accept: string, [extra: string]: * }) => Promise<Response>}
 */
export function createAuthedFetch({ fetchImpl = (...args) => fetch(...args), getHeaders = () => ({}), refreshAuth = async () => false } = {}) {
  return async function request(url, { method, body, signal, accept, ...extra }) {
    const init = () => ({ ...extra, method, body, signal, headers: { Accept: accept, ...getHeaders() } });
    const first = await fetchImpl(url, init());
    if (first.status !== HTTP_UNAUTHORIZED || !(await refreshAuth())) return first;
    return fetchImpl(url, init());
  };
}

/** True when the request was aborted (by its signal), not failed. */
export const isAbortError = (err, signal) => (signal && signal.aborted) || (err && err.name === 'AbortError');

/** The JSON body of a response, or null when it has none (a web server's own error page). */
export async function readJson(response) {
  try {
    return await response.json();
  } catch (err) {
    return null;
  }
}
